// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  CommonApi,
  Middleware,
  OperatorApi,
  RegistryApi,
  RequestContext,
  ResponseContext,
  ServerConfiguration,
  createConfiguration,
} from '@digitalasset/utility-operator-api-ts-client';
import React, { useMemo } from 'react';
import { BaseApiMiddleware } from '../utils/BaseApiMiddleware';
import { useUserState } from './UserContext';
import { UtilityCommonApiContext } from './UtilityCommonApiContext';
import { UtilityOperatorApiContext } from './UtilityOperatorApiContext';
import { UtilityRegistryApiContext } from './UtilityRegistryApiContext';

class ApiMiddleware
  extends BaseApiMiddleware<RequestContext, ResponseContext>
  implements Middleware {}

interface UtilityOperatorApiProps {
  utilityOperatorUrl: string;
}

export const UtilityOperatorProxyApiProvider = ({
  utilityOperatorUrl,
  children,
}: React.PropsWithChildren<UtilityOperatorApiProps>) => {
  const { loggedInUser } = useUserState();

  const clients = useMemo(() => {
    if (!loggedInUser) {
      return undefined;
    }
    const configuration = createConfiguration({
      baseServer: new ServerConfiguration(utilityOperatorUrl, {}),
      promiseMiddleware: [new ApiMiddleware(loggedInUser.userAccessToken)],
    });
    return {
      operator: new OperatorApi(configuration),
      registry: new RegistryApi(configuration),
      common: new CommonApi(configuration),
    };
  }, [utilityOperatorUrl, loggedInUser]);

  return (
    <UtilityOperatorApiContext.Provider value={clients?.operator}>
      <UtilityRegistryApiContext.Provider value={clients?.registry}>
        <UtilityCommonApiContext.Provider value={clients?.common}>
          {children}
        </UtilityCommonApiContext.Provider>
      </UtilityRegistryApiContext.Provider>
    </UtilityOperatorApiContext.Provider>
  );
};
